import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, PhoneOutgoing, PhoneCall, ArrowRightLeft, ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Clock, User, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  dialing: { label: 'Dialing', cls: 'bg-blue-100 text-blue-700' },
  ringing: { label: 'Ringing', cls: 'bg-indigo-100 text-indigo-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
  sim_active: { label: 'In Progress', cls: 'bg-sky-100 text-sky-700' },
  failed: { label: 'Failed', cls: 'bg-rose-100 text-rose-700' },
  transferring: { label: 'Transferred', cls: 'bg-amber-100 text-amber-700' },
  after_hours_overflow: { label: 'After-Hours', cls: 'bg-purple-100 text-purple-700' },
};
const meta = (s) => STATUS_META[s] || { label: s || 'Unknown', cls: 'bg-zinc-100 text-zinc-600' };
const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return '—'; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };

// Recording / call duration → m:ss (or "—" when unknown).
const fmtDur = (sec) => {
  const s = parseInt(sec, 10);
  if (!s || s < 0) return null;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')} min` : `${r} sec`;
};

const isUser = (role) => ['user', 'caller', 'customer'].includes((role || '').toLowerCase());

// Turn the stored transcript into chat turns: prefer structured transcript_turns,
// else parse the "role: text" lines of the merged transcript string.
const parseTurns = (call) => {
  if (Array.isArray(call.transcript_turns) && call.transcript_turns.length) {
    return call.transcript_turns.map(t => ({ role: t.role || 'aayush', text: t.text || '' })).filter(t => t.text);
  }
  if (call.transcript) {
    return String(call.transcript).split('\n').map(l => l.trim()).filter(Boolean).map(line => {
      const m = line.match(/^([\w@.\u0900-\u097F]+)\s*:\s*(.*)$/);
      return m ? { role: m[1], text: m[2] } : { role: 'aayush', text: line };
    }).filter(t => t.text);
  }
  return [];
};

// A single call inside a number's history (mobile-style log entry).
const CallEntry = ({ call }) => {
  const [showTxt, setShowTxt] = useState(false);
  const dur = fmtDur(call.duration_sec);
  const turns = parseTurns(call);
  return (
    <div className="py-3 px-3 sm:px-4 border-t border-zinc-100 first:border-t-0" data-testid={`call-entry-${call.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <PhoneCall className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <span className="text-xs text-zinc-500 whitespace-nowrap" data-testid={`call-time-${call.id}`}>{fmtTime(call.created_at)}</span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta(call.status).cls}`}>{meta(call.status).label}</span>
        {call.is_simulation && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 text-sky-600 border border-sky-100">Sandbox</span>}
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${dur ? 'bg-zinc-100 text-zinc-700' : 'text-zinc-400'}`} data-testid={`call-duration-${call.id}`}>
          <Clock className="w-3 h-3" /> {dur || '—'}
        </span>
        {call.handoff && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 inline-flex items-center gap-0.5"><ArrowRightLeft className="w-3 h-3" /> Handoff</span>}
        <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {call.has_recording ? (
            <audio controls preload="none" className="h-8 w-44" src={`${API_URL}/api/live-chat/recording/${call.id}`} data-testid={`call-log-audio-${call.id}`} />
          ) : <span className="text-xs text-zinc-300">no recording</span>}
        </div>
      </div>
      {(turns.length > 0 || call.handoff_reason || call.error) && (
        <div className="mt-2 pl-5">
          <button onClick={() => setShowTxt(v => !v)} className="text-[11px] font-medium text-orange-600 inline-flex items-center gap-0.5" data-testid={`call-transcript-toggle-${call.id}`}>
            <ChevronDown className={`w-3 h-3 transition-transform ${showTxt ? 'rotate-180' : ''}`} /> {showTxt ? 'Conversation छुपाएं' : `बातचीत देखें${turns.length ? ` (${turns.length})` : ''}`}
          </button>
          {showTxt && (
            <div className="mt-1.5 text-xs bg-zinc-50 rounded-xl p-2.5 border border-zinc-100" data-testid={`call-transcript-${call.id}`}>
              {call.handoff_reason && <p className="text-amber-700 mb-1.5">↪ Handoff: {call.handoff_reason}</p>}
              {call.error && <p className="text-rose-600 mb-1.5">⚠ {typeof call.error === 'string' ? call.error : JSON.stringify(call.error)}</p>}
              {turns.length ? (
                <div className="space-y-2 max-h-60 overflow-auto pr-1" data-testid={`call-chat-${call.id}`}>
                  {turns.map((t, i) => (
                    <div key={i} className={`flex ${isUser(t.role) ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 ${isUser(t.role) ? 'bg-orange-500 text-white rounded-br-sm' : 'bg-white text-zinc-700 border border-zinc-200 rounded-bl-sm'}`}>
                        <p className={`text-[10px] font-semibold mb-0.5 ${isUser(t.role) ? 'text-orange-100' : 'text-zinc-400'}`}>{isUser(t.role) ? '🙋 Customer' : '🤖 Aayush'}</p>
                        <p className="whitespace-pre-wrap leading-snug">{t.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-zinc-400">— कोई बातचीत record नहीं हुई —</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// One row per PHONE NUMBER (like a mobile call log). Expand to see every call to
// that number across different times, newest first — with name, duration & recording.
const NumberGroup = ({ group, open, onToggle, onOpenLead }) => {
  const latest = group.calls && group.calls[0];
  return (
    <div className="bg-white" data-testid={`call-group-${group.phone || 'unknown'}`}>
      <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={(e) => { if (e.key === 'Enter') onToggle(); }}
        className="w-full flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-zinc-50 text-left cursor-pointer" data-testid={`call-group-toggle-${group.phone || 'unknown'}`}>
        <div className="w-9 h-9 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
          {group.lead_name ? <User className="w-4 h-4" /> : <PhoneOutgoing className="w-4 h-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-zinc-900 text-sm truncate" data-testid={`call-group-name-${group.phone || 'unknown'}`}>
              {group.lead_name || group.phone || 'Unknown'}
            </span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-100 text-zinc-600">{group.call_count} {group.call_count > 1 ? 'calls' : 'call'}</span>
          </div>
          <p className="text-xs text-zinc-500 truncate">
            {group.lead_name ? <span className="font-medium text-zinc-600">{group.phone}</span> : null}
            {group.school_name ? <span>{group.lead_name ? ' · ' : ''}{group.school_name}</span> : null}
            <span>{(group.lead_name || group.school_name) ? ' · ' : ''}last {fmtTime(group.last_call_at)}</span>
          </p>
        </div>
        {latest && <span className={`hidden sm:inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta(latest.status).cls}`}>{meta(latest.status).label}</span>}
        {group.lead_id && onOpenLead && (
          <button onClick={(e) => { e.stopPropagation(); onOpenLead(group.lead_id); }}
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-orange-50 text-orange-600 border border-orange-100 hover:bg-orange-100 transition-colors"
            data-testid={`call-group-open-lead-${group.phone || 'unknown'}`}>
            <ExternalLink className="w-3 h-3" /> Lead खोलें
          </button>
        )}
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="bg-zinc-50/60" data-testid={`call-group-history-${group.phone || 'unknown'}`}>
          {(group.calls || []).map((c) => <CallEntry key={c.id} call={c} />)}
        </div>
      )}
    </div>
  );
};

// Platform Admin "Call Logs" dashboard — mobile-style log grouped by number:
// name + every call at its own time, duration, status, recording & transcript.
export const CallLogsView = ({ token, onOpenLead }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 30 });
      if (status) params.set('status', status);
      const res = await fetch(`${API_URL}/api/live-chat/admin/call-logs?${params}`, { headers });
      const d = await res.json();
      if (d.success) { setGroups(d.groups || []); setStats(d.stats || {}); setTotal(d.total); setPages(d.pages || 1); }
    } catch (e) { console.error('call logs', e); }
    finally { setLoading(false); }
  }, [token, page, status]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4 pb-28" data-testid="crm-call-logs">
      {/* Stat chips */}
      <div className="flex flex-wrap gap-2" data-testid="call-logs-stats">
        <button onClick={() => { setStatus(''); setPage(1); }}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${!status ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200'}`}
          data-testid="call-logs-filter-all">सभी Numbers ({total})</button>
        {Object.entries(stats).map(([k, v]) => (
          <button key={k} onClick={() => { setStatus(k); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${status === k ? 'bg-zinc-900 text-white border-zinc-900' : `${meta(k).cls} border-transparent`}`}
            data-testid={`call-logs-filter-${k}`}>{meta(k).label} ({v})</button>
        ))}
        <button onClick={fetchLogs} className="ml-auto px-3 py-1.5 rounded-full text-xs font-medium border border-zinc-200 text-zinc-600 inline-flex items-center gap-1 hover:bg-zinc-50" data-testid="call-logs-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> लोड हो रहा है…</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-zinc-400 text-sm" data-testid="call-logs-empty">अभी कोई call log नहीं</div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden divide-y divide-zinc-100" data-testid="call-logs-list">
          {groups.map((g) => {
            const key = g.phone || 'unknown';
            return <NumberGroup key={key} group={g} open={openKey === key} onToggle={() => setOpenKey(openKey === key ? null : key)} onOpenLead={onOpenLead} />;
          })}
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 text-sm">
            <span className="text-zinc-400 text-xs">Page {page} / {pages}</span>
            <div className="flex gap-1.5">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="call-logs-prev"
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-zinc-200 disabled:opacity-40 hover:bg-zinc-50"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="call-logs-next"
                className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-zinc-200 disabled:opacity-40 hover:bg-zinc-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallLogsView;
